/* Lumina Prime theme */
(() => {
  'use strict';

  const theme = window.theme || {};
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Money ---------- */
  function formatMoney(cents, trimZeros = false) {
    const value = Number(cents) / 100;
    const fmt = theme.moneyFormat || '${{amount}}';
    const withDelims = (n, decimals, thousands = ',', decimal = '.') => {
      const fixed = n.toFixed(decimals);
      const [whole, frac] = fixed.split('.');
      const w = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? w + decimal + frac : w;
    };
    const decimals = trimZeros && Number(cents) % 100 === 0 ? 0 : 2;
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelims(value, 0);
        case 'amount_with_comma_separator': return withDelims(value, decimals, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(value, 0, '.', ',');
        default: return withDelims(value, decimals);
      }
    });
  }

  /* ---------- Scroll lock & overlays ---------- */
  let lastFocus = null;
  function lock(on) { document.body.classList.toggle('is-locked', on); }

  function openPanel(panel, focusSel) {
    lastFocus = document.activeElement;
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    lock(true);
    document.dispatchEvent(new CustomEvent('overlay:change', { detail: { open: true } }));
    const target = focusSel ? $(focusSel, panel) : null;
    window.setTimeout(() => (target || panel).focus && (target || panel).focus({ preventScroll: true }), 60);
  }

  function closePanel(panel) {
    if (!panel.classList.contains('is-open')) return;
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    if (!$('.cart-drawer.is-open, .menu-drawer.is-open')) lock(false);
    document.dispatchEvent(new CustomEvent('overlay:change', { detail: { open: false } }));
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  }

  /* ---------- Header ---------- */
  function initHeader() {
    const header = $('[data-header]');
    if (header) {
      const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    }
    const drawer = $('[data-menu-drawer]');
    const openBtn = $('[data-menu-open]');
    if (drawer && openBtn) {
      openBtn.addEventListener('click', () => {
        openBtn.setAttribute('aria-expanded', 'true');
        openPanel(drawer, '[data-menu-close].header__icon');
      });
      $$('[data-menu-close]', drawer).forEach((el) => el.addEventListener('click', () => {
        openBtn.setAttribute('aria-expanded', 'false');
        closePanel(drawer);
      }));
    }
  }

  /* ---------- Cart ---------- */
  const cartDrawer = () => $('[data-cart-drawer]');

  function updateCartCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', Number(count) === 0);
      el.classList.remove('is-bump');
      void el.offsetWidth;
      el.classList.add('is-bump');
    });
  }

  function renderCartSection(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = $('[data-cart-content]', doc);
    const current = $('[data-cart-content]');
    if (fresh && current) {
      current.innerHTML = fresh.innerHTML;
      current.dataset.count = fresh.dataset.count;
      updateCartCount(fresh.dataset.count);
    }
  }

  async function refreshCart() {
    try {
      const res = await fetch(`${window.location.pathname}?sections=cart-drawer`);
      const data = await res.json();
      renderCartSection(data['cart-drawer']);
    } catch (e) { /* no-op */ }
  }

  function openCart() {
    const d = cartDrawer();
    if (d) openPanel(d, '[data-cart-close].header__icon');
  }

  async function cartRequest(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: 'cart-drawer', sections_url: window.location.pathname })
    });
    const data = await res.json();
    if (!res.ok || data.status) {
      throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
    }
    return data;
  }

  async function addToCart(items) {
    const data = await cartRequest(`${theme.routes.cartAdd}.js`, { items });
    renderCartSection(data.sections && data.sections['cart-drawer']);
    openCart();
    return data;
  }

  // Skip-cart mode: the checkout holds exactly what was just picked, so going back
  // and choosing a different bundle never stacks on top of an earlier selection.
  async function buyNow(items) {
    const cleared = await fetch(`${theme.routes.cartClear}.js`, { method: 'POST', headers: { Accept: 'application/json' } });
    if (!cleared.ok) throw new Error('Something went wrong. Please try again.');
    const res = await fetch(`${theme.routes.cartAdd}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    });
    const data = await res.json();
    if (!res.ok || data.status) {
      throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
    }
    window.location.href = theme.routes.checkout;
  }

  async function changeLine(line, quantity) {
    const drawer = $('[data-cart-content]');
    if (drawer) drawer.style.opacity = '.55';
    try {
      const data = await cartRequest(`${theme.routes.cartChange}.js`, { line: Number(line), quantity: Number(quantity) });
      if ($('[data-cart-page]')) { window.location.reload(); return; }
      renderCartSection(data.sections && data.sections['cart-drawer']);
    } catch (err) {
      toast(err.message);
    } finally {
      const d = $('[data-cart-content]');
      if (d) d.style.opacity = '';
    }
  }

  function initCart() {
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-cart-open]');
      if (opener && cartDrawer()) {
        e.preventDefault();
        openCart();
        return;
      }
      const closer = e.target.closest('[data-cart-close]');
      if (closer && cartDrawer()) {
        closePanel(cartDrawer());
        return;
      }
      const lineBtn = e.target.closest('[data-line-change]');
      if (lineBtn) {
        e.preventDefault();
        changeLine(lineBtn.dataset.lineChange, lineBtn.dataset.qtyNext);
        return;
      }
      const upsell = e.target.closest('[data-upsell-add]');
      if (upsell) {
        e.preventDefault();
        upsell.disabled = true;
        addToCart([{ id: Number(upsell.dataset.upsellAdd), quantity: 1 }]).catch((err) => toast(err.message)).finally(() => { upsell.disabled = false; });
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      $$('.cart-drawer.is-open, .menu-drawer.is-open').forEach(closePanel);
    });

    document.addEventListener('cart:refresh', refreshCart);
    window.addEventListener('pageshow', (e) => { if (e.persisted) refreshCart(); });
  }

  /* ---------- Toast ---------- */
  let toastTimer;
  function toast(msg) {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { el.hidden = true; }, 3800);
  }

  /* ---------- Buy box ---------- */
  function initBuyBox(box) {
    if (box.dataset.ready) return;
    box.dataset.ready = 'true';

    const d = box.dataset;
    const priceOnetime = Number(d.priceOnetime);
    const compareOnetime = Number(d.compareOnetime);
    const priceFirst = Number(d.priceFirst);
    const priceRecurring = Number(d.priceRecurring);
    const hasPlan = d.hasPlan === 'true';
    const form = $('[data-product-form]', box);
    const qtyInput = $('[data-qty-input]', box);
    const planInput = $('[data-plan-input]', box);
    const totalEl = $('[data-add-total]', box);
    const recurringEl = $('[data-recurring]', box);
    const subTerms = $('[data-sub-terms]', box);
    const errorEl = $('[data-form-error]', box);
    const section = box.closest('.shopify-section') || document;
    const tierImage = $('[data-tier-image]', section);
    const sticky = $('[data-sticky-cta]', section);
    const tiers = $$('.tier', box);

    const isSub = () => {
      if (!hasPlan) return false;
      const checked = $('[data-ptype]:checked', box);
      return checked ? checked.value === 'subscription' : true;
    };

    const prices = (qty, input) => {
      let now, was;
      if (isSub()) {
        if (input && input.dataset.deal) now = Number(input.dataset.deal);
        else now = (input && input.dataset.first ? Number(input.dataset.first) : priceFirst) * qty;
        was = Math.max(compareOnetime, priceOnetime) * qty;
      } else {
        now = input && input.dataset.deal ? Number(input.dataset.deal) : priceOnetime * qty;
        was = compareOnetime * qty;
      }
      return { now, was, save: Math.max(0, was - now), unit: Math.round(now / qty) };
    };

    const selectedTier = () => {
      const input = $('.tier__input:checked', box);
      return input ? input : null;
    };

    const render = (swapImage) => {
      tiers.forEach((tier) => {
        const input = $('.tier__input', tier);
        const qty = Number(input.dataset.qty);
        const p = prices(qty, input);
        tier.classList.toggle('is-selected', input.checked);
        const nowEl = $('[data-tier-now]', tier);
        const wasEl = $('[data-tier-was]', tier);
        const saveWrap = $('[data-tier-save]', tier);
        const saveAmt = $('[data-tier-save-amount]', tier);
        const unitEl = $('[data-tier-unit]', tier);
        if (nowEl) nowEl.textContent = formatMoney(p.now);
        if (wasEl) { wasEl.textContent = formatMoney(p.was); wasEl.hidden = p.save <= 0; }
        if (saveWrap) saveWrap.hidden = p.save <= 0;
        if (saveAmt) saveAmt.textContent = formatMoney(p.save, true);
        if (unitEl) unitEl.textContent = formatMoney(p.unit);
      });

      const sel = selectedTier();
      const qty = sel ? Number(sel.dataset.qty) : Number(qtyInput.value || 1);
      const p = prices(qty, sel);
      qtyInput.value = qty;
      if (totalEl) totalEl.textContent = formatMoney(p.now);
      if (recurringEl) recurringEl.textContent = formatMoney(priceRecurring * qty);
      const recurringUnit = $('[data-recurring-unit]', box);
      if (recurringUnit) recurringUnit.textContent = formatMoney(priceRecurring);
      if (planInput) planInput.disabled = !isSub();
      if (planInput && sel && sel.dataset.plan) planInput.value = sel.dataset.plan;
      if (subTerms) subTerms.hidden = !isSub();

      if (swapImage && tierImage && sel && sel.dataset.img && tierImage.dataset.current !== sel.dataset.img) {
        tierImage.classList.add('is-swapping');
        const next = new Image();
        next.onload = () => {
          tierImage.removeAttribute('srcset');
          tierImage.src = sel.dataset.img;
          tierImage.dataset.current = sel.dataset.img;
          requestAnimationFrame(() => tierImage.classList.remove('is-swapping'));
        };
        next.src = sel.dataset.img;
      }

      if (sticky) {
        const t = $('[data-sticky-title]', sticky);
        const pr = $('[data-sticky-price]', sticky);
        const was = $('[data-sticky-was]', sticky);
        if (t && sel) t.textContent = sel.dataset.title + (isSub() ? ' · Auto-refill' : '');
        if (pr) pr.textContent = formatMoney(p.now);
        if (was) { was.textContent = formatMoney(p.was); was.hidden = p.save <= 0; }
      }
    };

    box.addEventListener('change', (e) => {
      if (e.target.matches('.tier__input')) render(true);
      if (e.target.matches('[data-ptype]')) render(false);
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = $('[data-add-button]', form);
      if (errorEl) errorEl.hidden = true;
      btn.classList.add('is-loading');
      const item = { id: Number(form.querySelector('[name="id"]').value), quantity: Number(qtyInput.value || 1) };
      if (planInput && !planInput.disabled && planInput.value) item.selling_plan = Number(planInput.value);
      try {
        if (theme.cartType === 'checkout') {
          await buyNow([item]);
          return; // keep the spinner while the browser navigates
        }
        await addToCart([item]);
        btn.classList.remove('is-loading');
      } catch (err) {
        btn.classList.remove('is-loading');
        if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
        else toast(err.message);
      }
    });

    // Coming back from checkout with the browser's back button restores this page
    // from cache with the spinner still showing.
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) $('[data-add-button]', form).classList.remove('is-loading');
    });

    render(false);
    if (tierImage) tierImage.dataset.current = '';
    initSticky(box, sticky, form);
  }

  /* ---------- Sticky CTA ---------- */
  function initSticky(box, sticky, form) {
    if (!sticky || !('IntersectionObserver' in window)) return;
    let boxVisible = true;
    let overlayOpen = false;
    const update = () => {
      const show = !boxVisible && !overlayOpen && window.scrollY > window.innerHeight * 0.8;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      const btn = $('[data-sticky-add]', sticky);
      if (btn) btn.tabIndex = show ? 0 : -1;
    };
    new IntersectionObserver((entries) => {
      boxVisible = entries[0].isIntersecting;
      update();
    }, { rootMargin: '0px 0px -10% 0px' }).observe(box);
    const footer = $('.footer');
    if (footer) {
      new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) { sticky.classList.remove('is-visible'); } else { update(); }
      }).observe(footer);
    }
    window.addEventListener('scroll', update, { passive: true });
    document.addEventListener('overlay:change', () => {
      overlayOpen = !!$('.cart-drawer.is-open, .menu-drawer.is-open');
      update();
    });
    const btn = $('[data-sticky-add]', sticky);
    if (btn) btn.addEventListener('click', () => {
      if (form.requestSubmit) form.requestSubmit(); else form.dispatchEvent(new Event('submit', { cancelable: true }));
    });
  }

  /* ---------- Tabs ---------- */
  function initTabs(root) {
    $$('[data-tabs]', root).forEach((wrap) => {
      const btns = $$('[data-tab]', wrap);
      const panels = $$('[data-panel]', wrap);
      const activate = (i, focus) => {
        btns.forEach((b, j) => {
          const on = j === i;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-selected', on ? 'true' : 'false');
          b.tabIndex = on ? 0 : -1;
          if (on && focus) b.focus();
        });
        panels.forEach((p, j) => {
          const on = j === i;
          p.hidden = !on;
          p.classList.toggle('is-active', on);
        });
      };
      btns.forEach((b, i) => {
        b.addEventListener('click', () => activate(i));
        b.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') activate((i + 1) % btns.length, true);
          if (e.key === 'ArrowLeft') activate((i - 1 + btns.length) % btns.length, true);
        });
      });
    });
  }

  /* ---------- Dialogs ---------- */
  function initDialogs() {
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-dialog-open]');
      if (opener) {
        const dlg = document.getElementById(opener.dataset.dialogOpen);
        if (dlg && dlg.showModal) dlg.showModal();
        return;
      }
      if (e.target.closest('[data-dialog-close]')) {
        const dlg = e.target.closest('dialog');
        if (dlg) dlg.close();
        return;
      }
      if (e.target.tagName === 'DIALOG') e.target.close();
    });
  }

  /* ---------- UGC videos ---------- */
  function initVideos(root) {
    const cards = $$('.ugc__card', root);
    if (!cards.length) return;
    const load = (video) => {
      const src = $('source[data-src]', video);
      if (src && !src.src) { src.src = src.dataset.src; video.load(); }
    };
    const play = (card) => {
      const v = $('video', card);
      if (!v) return;
      load(v);
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
      card.classList.add('is-playing');
    };
    const pause = (card) => {
      const v = $('video', card);
      if (!v) return;
      v.pause();
      card.classList.remove('is-playing');
    };
    if ('IntersectionObserver' in window && !reduceMotion) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => (en.isIntersecting ? play(en.target) : pause(en.target)));
      }, { threshold: 0.6 });
      cards.forEach((c) => io.observe(c));
    }
    cards.forEach((card) => {
      card.addEventListener('click', () => {
        const v = $('video', card);
        if (!v) return;
        cards.forEach((other) => {
          const ov = $('video', other);
          if (ov && other !== card) { ov.muted = true; other.classList.remove('is-unmuted'); }
        });
        v.muted = !v.muted;
        card.classList.toggle('is-unmuted', !v.muted);
        play(card);
      });
    });
  }

  /* ---------- Gallery ---------- */
  function initGallery(root) {
    $$('[data-gallery]', root).forEach((g) => {
      const track = $('[data-gallery-track]', g);
      const slides = $$('.gallery__slide', track);
      const dots = $$('.gallery__dot', g);
      const thumbs = $$('[data-thumb]', g.parentElement);
      if (slides.length < 2) return;
      let current = 0;
      const go = (i) => {
        const idx = (i + slides.length) % slides.length;
        track.scrollTo({ left: slides[idx].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      };
      const mark = (i) => {
        current = i;
        dots.forEach((dEl, j) => dEl.classList.toggle('is-active', j === i));
        thumbs.forEach((t, j) => t.classList.toggle('is-active', j === i));
      };
      let ticking = false;
      track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          mark(Math.round(track.scrollLeft / track.clientWidth));
          ticking = false;
        });
      }, { passive: true });
      const prev = $('[data-gallery-prev]', g);
      const next = $('[data-gallery-next]', g);
      if (prev) prev.addEventListener('click', () => go(current - 1));
      if (next) next.addEventListener('click', () => go(current + 1));
      thumbs.forEach((t) => t.addEventListener('click', () => go(Number(t.dataset.thumb))));
    });
  }

  /* ---------- Reveal & count-up ---------- */
  function countUp(el) {
    if (el.dataset.counted) return;
    el.dataset.counted = 'true';
    const target = Number(el.dataset.countTo);
    const suffix = el.dataset.countSuffix || '';
    const decimals = Number(el.dataset.countDecimals || 0);
    if (el.dataset.countTo === '' || Number.isNaN(target)) return;
    const fmt = (n) => n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + suffix;
    if (reduceMotion || target === 0) { el.textContent = fmt(target); return; }
    const duration = 1400;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = fmt(target * eased);
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  function initReveal(root) {
    const items = $$('.reveal:not(.is-in)', root);
    const counters = $$('[data-count-to]', root);
    if (!('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      counters.forEach(countUp);
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        en.target.classList.add('is-in');
        io.unobserve(en.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach((el) => io.observe(el));
    const co = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        countUp(en.target);
        co.unobserve(en.target);
      });
    }, { threshold: 0.5 });
    counters.forEach((el) => co.observe(el));
  }

  /* ---------- Timeline progress ---------- */
  function initTimeline(root) {
    $$('[data-progress-line]', root).forEach((wrap) => {
      const steps = $$('.step', wrap);
      const onScroll = () => {
        const rect = wrap.getBoundingClientRect();
        const mid = window.innerHeight * 0.6;
        const progress = Math.min(1, Math.max(0, (mid - rect.top) / rect.height));
        wrap.style.setProperty('--progress', progress.toFixed(3));
        steps.forEach((s) => {
          const r = s.getBoundingClientRect();
          s.classList.toggle('is-reached', r.top + 30 < mid);
        });
      };
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
    });
  }

  /* ---------- Delivery estimate ---------- */
  function addBusinessDays(date, days) {
    const d = new Date(date);
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    return d;
  }

  function initEta(root) {
    $$('[data-eta]', root).forEach((el) => {
      const min = Number(el.dataset.etaMin || 4);
      const max = Number(el.dataset.etaMax || 8);
      const fmt = { weekday: 'short', month: 'short', day: 'numeric' };
      const now = new Date();
      const a = addBusinessDays(now, min).toLocaleDateString('en-US', fmt);
      const b = addBusinessDays(now, max).toLocaleDateString('en-US', fmt);
      const out = $('[data-eta-text]', el);
      if (out) out.textContent = `${a} – ${b}`;
    });
  }

  /* ---------- Init ---------- */
  function initSection(root = document) {
    $$('[data-buybox]', root).forEach(initBuyBox);
    initTabs(root);
    initVideos(root);
    initGallery(root);
    initReveal(root);
    initTimeline(root);
    initEta(root);
  }

  function boot() {
    initHeader();
    initCart();
    initDialogs();
    initSection(document);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (e) => initSection(e.target));
})();
